import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setLocaleProvider } from "@/lib/api";
import {
  acceptLanguageHeader,
  formatMessage,
  messagesFor,
  readStoredLocale,
  writeStoredLocale,
  type Locale,
  type Messages,
} from "./index";

type LocaleContextValue = {
  locale: Locale;
  m: Messages;
  setLocale: (locale: Locale) => void;
  fmt: (template: string, vars?: Record<string, string | number>) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    setLocaleProvider(() => acceptLanguageHeader(locale));
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => {
    const m = messagesFor(locale);
    return {
      locale,
      m,
      setLocale,
      fmt: (template, vars) => formatMessage(template, vars),
    };
  }, [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale requires LocaleProvider");
  return ctx;
}
